import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

import StickyFooter from '../components/StickyFooter';

interface Keynote {
  id: string;
  title: string;
  description?: string;
  speaker: string;
  duration?: string;
  topic?: string;
  targetAudience?: string;
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  date?: string;
  time?: string;
  price?: string;
  maxAttendees?: number;
  currentAttendees?: number;
  imageUrl?: string;
  speakerBio?: string;
  objectives?: string;
  prerequisites?: string;
  url?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

interface AdminKeynoteEditorProps {
  selectedKeynoteId?: string;
  onBack?: () => void;
}

const AdminKeynoteEditor: React.FC<AdminKeynoteEditorProps> = ({ selectedKeynoteId: propSelectedKeynoteId, onBack }) => {
  const [selectedKeynoteId, setSelectedKeynoteId] = useState<string>(propSelectedKeynoteId || '');
  const [availableKeynotes, setAvailableKeynotes] = useState<{ id: string; title: string; speaker: string }[]>([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    speaker: '',
    duration: '',
    topic: '',
    targetAudience: '',
    street: '',
    houseNumber: '',
    zip: '',
    city: '',
    date: '',
    time: '',
    price: '',
    maxAttendees: 100,
    currentAttendees: 0,
    imageUrl: '',
    speakerBio: '',
    objectives: '',
    prerequisites: '',
    url: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);

  const isEditMode = Boolean(selectedKeynoteId);

  useEffect(() => {
    loadAvailableKeynotes();
  }, []);

  useEffect(() => {
    if (propSelectedKeynoteId) {
      setSelectedKeynoteId(propSelectedKeynoteId);
      loadKeynote(propSelectedKeynoteId);
    }
  }, [propSelectedKeynoteId]);

  useEffect(() => {
    if (isEditMode && selectedKeynoteId) {
      loadKeynote(selectedKeynoteId);
    }
  }, [selectedKeynoteId, isEditMode]);

  const loadAvailableKeynotes = async () => {
    try {
      console.log('🔍 Loading available keynotes...');
      const { data } = await client.models.Keynote.list({
        selectionSet: ['id', 'title', 'speaker']
      });
      console.log('📝 Available keynotes loaded:', data);
      
      const keynotes = (data || []).map((keynote: any) => ({
        id: keynote.id,
        title: keynote.title || 'Untitled Keynote',
        speaker: keynote.speaker || 'Unknown Speaker'
      }));
      
      setAvailableKeynotes(keynotes);
    } catch (error) {
      console.error('❌ Error loading available keynotes:', error);
      setAvailableKeynotes([]);
    }
  };

  const handleKeynoteSelection = (keynoteId: string) => {
    setSelectedKeynoteId(keynoteId);
    if (keynoteId) {
      loadKeynote(keynoteId);
    } else {
      // Reset to create mode
      setFormData({
        title: '',
        description: '',
        speaker: '',
        duration: '',
        topic: '',
        targetAudience: '',
        street: '',
        houseNumber: '',
        zip: '',
        city: '',
        date: '',
        time: '',
        price: '',
        maxAttendees: 100,
        currentAttendees: 0,
        imageUrl: '',
        speakerBio: '',
        objectives: '',
        prerequisites: '',
        url: '',
        isActive: true
      });
    }
    setErrors({});
  };

  const loadKeynote = async (id: string) => {
    setLoading(true);
    try {
      console.log('🔍 Loading keynote with ID:', id);
      const { data } = await client.models.Keynote.get({ 
        id,
        selectionSet: [
          'id',
          'title',
          'description',
          'speaker',
          'duration',
          'topic',
          'targetAudience',
          'street',
          'houseNumber',
          'zip',
          'city',
          'date',
          'time',
          'price',
          'maxAttendees',
          'currentAttendees',
          'imageUrl',
          'speakerBio',
          'objectives',
          'prerequisites',
          'url',
          'isActive'
        ]
      });
      console.log('📝 Keynote loaded:', data);
      
      if (data) {
        setFormData({
          title: data.title || '',
          description: data.description || '',
          speaker: data.speaker || '',
          duration: data.duration || '',
          topic: data.topic || '',
          targetAudience: data.targetAudience || '',
          street: data.street || '',
          houseNumber: data.houseNumber || '',
          zip: data.zip || '',
          city: data.city || '',
          date: data.date || '',
          time: data.time || '',
          price: data.price || '',
          maxAttendees: data.maxAttendees || 100,
          currentAttendees: data.currentAttendees || 0,
          imageUrl: data.imageUrl || '',
          speakerBio: data.speakerBio || '',
          objectives: data.objectives || '',
          prerequisites: data.prerequisites || '',
          url: data.url || '',
          isActive: data.isActive ?? true
        });
      }
    } catch (error) {
      console.error('❌ Error loading keynote:', error);
      setMessage({ type: 'error', text: 'Failed to load keynote. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }

    setSaving(true);
    try {
      console.log('🔧 Keynote submission started');
      
      // Prepare keynote data - include all fields including optional ones
      const keynoteData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        speaker: formData.speaker.trim(),
        duration: formData.duration.trim(),
        topic: formData.topic.trim(),
        targetAudience: formData.targetAudience.trim(),
        street: formData.street.trim(),
        houseNumber: formData.houseNumber.trim(),
        zip: formData.zip.trim(),
        city: formData.city.trim(),
        date: formData.date.trim(),
        time: formData.time.trim(),
        price: formData.price.trim(),
        maxAttendees: formData.maxAttendees,
        currentAttendees: formData.currentAttendees,
        imageUrl: formData.imageUrl.trim(),
        speakerBio: formData.speakerBio.trim(),
        objectives: formData.objectives.trim(),
        prerequisites: formData.prerequisites.trim(),
        url: formData.url.trim(),
        isActive: formData.isActive
      };
      
      console.log('📝 Creating/updating keynote with data:', keynoteData);

      let result;
      if (isEditMode && selectedKeynoteId) {
        // Update existing keynote
        result = await client.models.Keynote.update({
          id: selectedKeynoteId,
          ...keynoteData
        });
        console.log('✅ Keynote update result:', result);
      } else {
        // Create new keynote
        result = await client.models.Keynote.create(keynoteData);
        console.log('✅ Keynote creation result:', result);
      }

      if (result?.data) {
        if (!isEditMode) {
          // Reset form for new keynote
          setFormData({
            title: '',
            description: '',
            speaker: '',
            duration: '',
            topic: '',
            targetAudience: '',
            street: '',
            houseNumber: '',
            zip: '',
            city: '',
            date: '',
            time: '',
            price: '',
            maxAttendees: 100,
            currentAttendees: 0,
            imageUrl: '',
            speakerBio: '',
            objectives: '',
            prerequisites: '',
            url: '',
            isActive: true
          });
          // Reload available keynotes list
          await loadAvailableKeynotes();
        }

        setMessage({ type: 'success', text: `Keynote ${isEditMode ? 'updated' : 'created'} successfully!` });
      } else {
        throw new Error('No data returned from server');
      }
    } catch (err: any) {
      console.error('❌ Failed to save keynote:', {
        name: (err as any)?.name,
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        errors: (err as any)?.errors
      });
      
      let errorMessage = 'Failed to save keynote. Please try again.';
      if ((err as any)?.errors?.[0]?.message) {
        errorMessage = `GraphQL error: ${(err as any).errors[0].message}`;
      } else if ((err as any)?.message) {
        errorMessage = `Error: ${(err as any).message}`;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Always clear the form
    setFormData({
      title: '',
      description: '',
      speaker: '',
      duration: '',
      topic: '',
      targetAudience: '',
      street: '',
      houseNumber: '',
      zip: '',
      city: '',
      date: '',
      time: '',
      price: '',
      maxAttendees: 100,
      currentAttendees: 0,
      imageUrl: '',
      speakerBio: '',
      objectives: '',
      prerequisites: '',
      url: '',
      isActive: true
    });
    setErrors({});
  };

  const createSlug = (text: string) => {
    return (text || '')
      .toLowerCase()
      .replace(/[^a-z0-9\-\s]+/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Inject selector into header
  React.useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="keynoteSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
        >
          <option value="" ${selectedKeynoteId === '' ? 'selected' : ''}>Create New Keynote</option>
          ${availableKeynotes.map(keynote => 
            `<option value="${keynote.id}" ${selectedKeynoteId === keynote.id ? 'selected' : ''}>${keynote.title} - ${keynote.speaker}</option>`
          ).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handleKeynoteSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedKeynoteId, availableKeynotes]);

  if (loading) {
    return (
      <div className="admin-form">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading keynote...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-form admin-editor-content">

      
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="title">Keynote Title</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter keynote title"
            />
          </div>

          <div className="form-field">
            <label htmlFor="speaker">Speaker</label>
            <input
              id="speaker"
              type="text"
              value={formData.speaker}
              onChange={(e) => handleInputChange('speaker', e.target.value)}
              placeholder="Speaker name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="topic">Topic</label>
            <input
              id="topic"
              type="text"
              value={formData.topic}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              placeholder="Main topic or theme"
            />
          </div>

          <div className="form-field">
            <label htmlFor="duration">Duration</label>
            <input
              id="duration"
              type="text"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              placeholder="e.g., 2 hours, 90 minutes"
            />
          </div>

          <div className="form-field">
            <label htmlFor="targetAudience">Target Audience</label>
            <input
              id="targetAudience"
              type="text"
              value={formData.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e.target.value)}
              placeholder="e.g., C-level executives, developers"
            />
          </div>

          <div className="form-field">
            <label htmlFor="street">Street</label>
            <input
              id="street"
              type="text"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              placeholder="e.g., Musterstraße"
            />
          </div>

          <div className="form-field">
            <label htmlFor="houseNumber">House Number</label>
            <input
              id="houseNumber"
              type="text"
              value={formData.houseNumber}
              onChange={(e) => handleInputChange('houseNumber', e.target.value)}
              placeholder="e.g., 123"
            />
          </div>

          <div className="form-field">
            <label htmlFor="zip">ZIP Code</label>
            <input
              id="zip"
              type="text"
              value={formData.zip}
              onChange={(e) => handleInputChange('zip', e.target.value)}
              placeholder="e.g., 12345"
            />
          </div>

          <div className="form-field">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="e.g., Berlin"
            />
          </div>

          <div className="form-field">
            <label htmlFor="date">Date</label>
            <input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="time">Time</label>
            <input
              id="time"
              type="time"
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="price">Price (€)</label>
            <input
              id="price"
              type="text"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="e.g., 299.00"
            />
          </div>

          <div className="form-field">
            <label htmlFor="maxAttendees">Max Attendees</label>
            <input
              id="maxAttendees"
              type="number"
              value={formData.maxAttendees}
              onChange={(e) => handleInputChange('maxAttendees', parseInt(e.target.value) || 0)}
              min="1"
            />
          </div>

          <div className="form-field">
            <label htmlFor="currentAttendees">Current Attendees</label>
            <input
              id="currentAttendees"
              type="number"
              value={formData.currentAttendees}
              onChange={(e) => handleInputChange('currentAttendees', parseInt(e.target.value) || 0)}
              min="0"
            />
          </div>

          <div className="form-field">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              id="imageUrl"
              type="url"
              value={formData.imageUrl}
              onChange={(e) => handleInputChange('imageUrl', e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Detailed description of the keynote"
            rows={4}
          />
        </div>

        <div className="form-field">
          <label htmlFor="speakerBio">Speaker Bio</label>
          <textarea
            id="speakerBio"
            value={formData.speakerBio}
            onChange={(e) => handleInputChange('speakerBio', e.target.value)}
            placeholder="Brief biography of the speaker"
            rows={3}
          />
        </div>

        <div className="form-field">
          <label htmlFor="objectives">Learning Objectives</label>
          <textarea
            id="objectives"
            value={formData.objectives}
            onChange={(e) => handleInputChange('objectives', e.target.value)}
            placeholder="What attendees will learn or gain from this keynote"
            rows={3}
          />
        </div>

        <div className="form-field">
          <label htmlFor="prerequisites">Prerequisites</label>
          <textarea
            id="prerequisites"
            value={formData.prerequisites}
            onChange={(e) => handleInputChange('prerequisites', e.target.value)}
            placeholder="Any prerequisites or recommended background knowledge"
            rows={2}
          />
        </div>

        <div className="form-field">
          <label htmlFor="url">External URL</label>
          <input
            id="url"
            type="url"
            value={formData.url}
            onChange={(e) => handleInputChange('url', e.target.value)}
            placeholder="https://external-site.com/keynote-details"
          />
          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            Optional external URL for keynote details or registration
          </small>
        </div>

        <div className="form-field">
          <label>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Active (visible to users)
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="admin-btn secondary"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="button"
            className="admin-btn"
            disabled={!formData.title}
            onClick={() => {
              if (formData.url) {
                window.location.href = formData.url;
              } else {
                // Fallback to keynotes overview if no URL is set
                window.location.href = `/ki-impulse`;
              }
            }}
            title={formData.title ? 'Visit keynotes overview' : 'Enter title to enable'}
          >
            Visit
          </button>
        </div>
      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
      />
    </div>
  );
};

export default AdminKeynoteEditor;
